import styled from '@emotion/styled'

import { Label, LabelProps } from './Form/Label'
import { Icon } from './Icon'
import { Color, theme } from '../constants'
import { StyleProps, createStyles } from '../utils'

type CheckboxStyle = {
  checkedBackgroundColor: Color
  checkedIconColor: Color
  border?: string
}

const CHECKBOX_STYLES: Record<CheckboxVariant, CheckboxStyle> = {
  blue: {
    checkedBackgroundColor: Color.BLUE,
    checkedIconColor: Color.WHITE,
  },
  'light-blue': {
    checkedBackgroundColor: Color.LIGHTER_BLUE,
    checkedIconColor: Color.BLUE,
  },
  inverted: {
    checkedBackgroundColor: Color.WHITE,
    checkedIconColor: Color.BLUE,
    border: theme.border.style.thinDark,
  },
}

const DEFAULT_CHECKBOX_COLOR = 'blue'

const SIZES = {
  sm: 16,
  md: 20,
}

const getSize = (size?: CheckboxSize) => SIZES[size ?? 'md']

const CheckboxIcon = styled(Icon)<
  Pick<CheckboxProps, 'variant' | 'indeterminate'>
>((props) => {
  const style = CHECKBOX_STYLES[props.variant ?? DEFAULT_CHECKBOX_COLOR]

  return {
    bottom: 0,
    color: props.indeterminate ? Color.LIGHT_BLUE : style.checkedIconColor,
    left: 0,
    margin: 'auto',
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
    right: 0,
    top: 0,
    transform: 'scale(0.8)',
    transition: `opacity ${theme.animation.transition}, transform ${theme.animation.transition}`,
    zIndex: 1,
    '@media (prefers-reduced-motion)': {
      transitionDuration: '0ms',
    },
  }
})

const CheckboxInput = styled.input<
  Pick<CheckboxProps, 'variant' | 'indeterminate'> & {
    checkboxSize?: CheckboxSize
  }
>((props) => {
  const size = getSize(props.checkboxSize)
  const style = CHECKBOX_STYLES[props.variant ?? DEFAULT_CHECKBOX_COLOR]

  return {
    appearance: 'none',
    backgroundColor: Color.WHITE,
    border: style.border ?? `1px solid ${theme.color.border}`,
    borderRadius:
      props.checkboxSize === 'sm'
        ? theme.border.radius.small
        : theme.border.radius.normal,
    cursor: 'pointer',
    display: 'block',
    flexShrink: 0,
    height: size,
    margin: 0,
    padding: 0,
    transition: `border-color ${theme.animation.transition}, background-color ${theme.animation.transition}`,
    width: size,
    '&:checked': {
      backgroundColor: props.indeterminate
        ? Color.LIGHTEST_BLUE
        : style.checkedBackgroundColor,
      borderColor:
        style?.border ??
        (props.indeterminate
          ? Color.TRANSPARENT_BLUE
          : style.checkedBackgroundColor),
      '& + *': {
        opacity: 1,
        transform: 'scale(1)',
      },
    },
    '&:disabled': {
      backgroundColor: Color.DARK_MEDIUM_GREY,
      borderColor: Color.DARK_MEDIUM_GREY,
      cursor: 'not-allowed',
      '& + *': {
        color: Color.DARKEST_MEDIUM_GREY,
      },
    },
  }
})

const CheckboxRoot = styled.div<
  { checkboxSize?: CheckboxSize } & Partial<LabelProps>
>((props) => {
  const size = getSize(props.checkboxSize)

  return {
    alignItems: 'center',
    gridGap: 8,
    height: size,
    position: 'relative',
    width: size,
    ...createStyles(props.styles),
  }
})

export type CheckboxSize = 'sm' | 'md'

export type CheckboxVariant = 'blue' | 'light-blue' | 'inverted'

export type CheckboxProps = {
  checked: boolean
  disabled?: boolean
  indeterminate?: boolean
  label?: string
  labelColor?: Color
  onChange?: (checked: boolean) => void
  size?: CheckboxSize
  variant?: CheckboxVariant
  labelPlacement?: 'right' | 'left'
  styles?: StyleProps
}

export const Checkbox = (props: CheckboxProps) => {
  const input = (
    <CheckboxRoot
      checkboxSize={props.size}
      styles={props.label ? undefined : props.styles}
    >
      <CheckboxInput
        checkboxSize={props.size}
        checked={props.checked}
        disabled={props.disabled}
        indeterminate={props.indeterminate}
        onChange={(event) => props.onChange?.(event.target.checked)}
        type="checkbox"
        variant={props.variant}
      />
      <CheckboxIcon
        indeterminate={props.indeterminate}
        name={props.indeterminate ? 'MinusSmIcon' : 'CheckIcon'}
        size={props.size}
        variant={props.variant}
      />
    </CheckboxRoot>
  )

  return props.label ? (
    <Label
      color={props.labelColor}
      cursor={props.disabled ? 'not-allowed' : 'pointer'}
      placement={props.labelPlacement ?? 'right'}
      size={props.size}
      styles={props.styles}
      text={props.label}
    >
      {input}
    </Label>
  ) : (
    input
  )
}
